import { Box, Menu, MenuItem, ListItemText, Tooltip } from "@mui/material";
import { FC, useState, MouseEvent } from "react";
import { PosButton } from "../../../atoms";
import { KeyboardArrowDown, Check } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { updateCartSessionItem } from "../../../../redux/actions/cartAction";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { cloneDeep } from "lodash";
import { Colors } from "../../../../configs/colors";

type ProcessingOption = "Press Only" | "Clean Only" | "Alteration Only";
type SpecialRequest = "Redo" | ProcessingOption;

const SPECIAL_REQUESTS: SpecialRequest[] = ["Redo", "Press Only", "Clean Only", "Alteration Only"];

const SpecialRequestBadge: FC = () => (
    <Box
        sx={{
            width: "1.86rem",
            height: "1.86rem",
            borderRadius: "50%",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            background: "rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }}
    >
        <Box component="span" sx={{ fontSize: "var(--fs-xs)", fontWeight: 600, lineHeight: 1, color: Colors.WHITE }}>
            !
        </Box>
    </Box>
);

const ServiceActionButtons: FC = () => {
    const dispatch = useAppDispatch();
    const { selectedCartItem, selectedServiceType, selectedSession } = useAppSelector((state) => state.cart);
    const { activeConfigs } = useAppSelector((state) => state.priceOverrideConfig);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Press Only / Clean Only is only offered for a service that has an
    // active price override set up for it (Admin > Price Overrides), e.g.
    // DC, CS and HH - without one it would just be the regular price.
    const serviceId = selectedCartItem?.serviceType?.id ?? selectedServiceType?.id;
    const hasProcessingPricing = (processingType: "PRESS_ONLY" | "CLEAN_ONLY") =>
        (activeConfigs ?? []).some(
            (config) => config.categoryId === serviceId && config.processingType === processingType
        );

    // A bill can't mix Press Only and Clean Only items, so once another item
    // in the bill has one, the other is not offered.
    const otherItems = (selectedSession?.cartItems ?? []).filter(
        (item) => item.finalized && item.itemId !== selectedCartItem?.itemId
    );
    const billHasPressOnly = otherItems.some((item) => item.pressOnly);
    const billHasCleanOnly = otherItems.some((item) => item.cleanOnly);

    const disabledReason = (option: SpecialRequest): string => {
        switch (option) {
            case "Alteration Only":
                return hasAlterations ? "" : "Add an alteration first";
            case "Press Only":
                if (billHasCleanOnly) return "This bill already has Clean Only items";
                return hasProcessingPricing("PRESS_ONLY") || isSelected(option)
                    ? ""
                    : "No Press Only pricing set up for this service";
            case "Clean Only":
                if (billHasPressOnly) return "This bill already has Press Only items";
                return hasProcessingPricing("CLEAN_ONLY") || isSelected(option)
                    ? ""
                    : "No Clean Only pricing set up for this service";
            default:
                return "";
        }
    };

    const handleMenuItemClick = (option: SpecialRequest) => {
        handleClose();
        if (disabledReason(option)) return;
        if (option === "Redo") {
            dispatch(CartSliceAction.setIsRedoModalOpen(true));
            return;
        }
        if (selectedCartItem) {
            const clonedCartItem = cloneDeep(selectedCartItem);
            clonedCartItem.pressOnly = option === "Press Only";
            clonedCartItem.cleanOnly = option === "Clean Only";
            clonedCartItem.alterationOnly = option === "Alteration Only";
            dispatch(updateCartSessionItem({ cartItem: clonedCartItem }));
        }
    };

    const isSelected = (option: SpecialRequest) => {
        if (!selectedCartItem) return false;
        switch (option) {
            case "Redo": return !!selectedCartItem.redoDetails;
            case "Press Only": return !!selectedCartItem.pressOnly;
            case "Clean Only": return !!selectedCartItem.cleanOnly;
            case "Alteration Only": return !!selectedCartItem.alterationOnly;
        }
    };

    // Redo can coexist with a processing type, so show every active request.
    const selectedRequests = SPECIAL_REQUESTS.filter(isSelected);
    // "Alteration Only" only makes sense once the item actually has an
    // alteration attached - otherwise there'd be nothing for the zeroed
    // base price to be "instead of".
    const hasAlterations = !!selectedCartItem?.alterationItems?.length;

    return (
        <Box
            sx={{
                height: "5.857142857142857rem",
                flex: 1,
                minWidth: 0,
                // Size from the row, never from the label: otherwise the nowrap
                // text sets a min-width that pushes the check-in columns off-screen.
                contain: "inline-size",
                borderRadius: "1.43rem",
                overflow: "hidden",
                boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)",
            }}
        >
            <PosButton
                variantType="secondary"
                name="special-requests-trigger"
                onClick={handleClick}
                disabled={!selectedCartItem}
                sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 1.71rem",
                    gap: "0.4rem",
                    whiteSpace: "nowrap",
                    transition: "background-color 0.2s ease",
                    "&:hover": { background: "#636363 !important" },
                    "&.Mui-disabled": {
                        background: Colors.MAIN_GRAY,
                        color: Colors.WHITE,
                        opacity: 1,
                        cursor: "not-allowed",
                        pointerEvents: "auto"
                    }
                }}
            >
                {selectedRequests.length === 0 ? (
                    <Box
                        component="span"
                        sx={{
                            fontSize: "var(--fs-2xl)",
                            fontWeight: 600,
                            letterSpacing: "0.02rem",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        Special Requests
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, overflow: "hidden" }}>
                        <Box
                            component="span"
                            sx={{
                                fontSize: "var(--fs-2xs)",
                                fontWeight: 500,
                                letterSpacing: "0.03rem",
                                textTransform: "uppercase",
                                color: "rgba(255, 255, 255, 0.7)",
                                lineHeight: 1.2,
                            }}
                        >
                            Special Requests
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "0.57rem", marginTop: "0.29rem", minWidth: 0, maxWidth: "100%" }}>
                            <SpecialRequestBadge />
                            <Box
                                component="span"
                                sx={{
                                    fontSize: "var(--fs-base)",
                                    fontWeight: 600,
                                    letterSpacing: "0.02rem",
                                    lineHeight: 1.2,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {selectedRequests.join(", ")}
                            </Box>
                        </Box>
                    </Box>
                )}
                <KeyboardArrowDown sx={{ fontSize: "2.57rem", color: "rgba(255, 255, 255, 0.9)", flexShrink: 0 }} />
            </PosButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                MenuListProps={{ sx: { padding: 0 } }}
                sx={{
                    "& .MuiPaper-root": {
                        marginTop: "-0.71rem",
                        borderRadius: "1.57rem",
                        border: "1px solid #D9D9D9",
                        boxShadow: "0 14px 34px rgba(0, 0, 0, 0.18)",
                        width: anchorEl ? `${anchorEl.clientWidth}px` : "auto",
                        overflow: "hidden",
                    }
                }}
            >
                {SPECIAL_REQUESTS.map((option, index) => {
                    const reason = disabledReason(option);
                    const disabled = Boolean(reason);
                    const item = (
                        <MenuItem
                            key={option}
                            onClick={() => handleMenuItemClick(option)}
                            disabled={disabled}
                            sx={{
                                gap: "0.86rem",
                                padding: "1.14rem 1.43rem",
                                borderTop: index === 0 ? "none" : "1px solid #EFEFEF",
                                transition: "background-color 0.15s ease",
                                "&:hover": { background: "#F5F9FC" },
                            }}
                        >
                            <Box
                                sx={{
                                    width: "0.86rem",
                                    height: "0.86rem",
                                    borderRadius: "50%",
                                    background: Colors.PRIMARY,
                                    opacity: 0.8,
                                    flexShrink: 0,
                                }}
                            />
                            <ListItemText
                                primary={option}
                                primaryTypographyProps={{ fontSize: "var(--fs-base)", fontWeight: 500, color: Colors.MAIN_GRAY }}
                            />
                            {isSelected(option) && <Check sx={{ fontSize: "1.71rem", color: Colors.PRIMARY }} />}
                        </MenuItem>
                    );
                    return option === "Redo" ? item : (
                        <Tooltip key={option} title={reason} placement="right">
                            <span>{item}</span>
                        </Tooltip>
                    );
                })}
            </Menu>
        </Box>
    );
};

export default ServiceActionButtons;
